import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';

// Geodaten liegen auf dem eigenen Server: kein Aufruf an ein fremdes CDN
// und damit keine Datenübertragung vor einer Zustimmung.
const geoUrl = '/data/countries-110m.json';

export default function WorldMap({ locations = [] }) {
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [animatedMarkers, setAnimatedMarkers] = useState([]);
  const mapRef = useRef(null);

  // Animate markers sequentially on mount
  useEffect(() => {
    if (locations.length > 0) {
      const timer = setTimeout(() => {
        locations.forEach((location, index) => {
          setTimeout(() => {
            setAnimatedMarkers(prev => [...prev, location.id]);
          }, index * 200);
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [locations]);

  return (
    <section className="py-16 md:py-20 bg-card/30" id="locations">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          <span className="kicker mb-3">
            Global Presence
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-foreground">
            Where We Create
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative w-full max-w-3xl mx-auto"
          ref={mapRef}
        >
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{
              scale: 140,
              center: [10, 10],
            }}
            style={{ width: '100%', height: 'auto' }}
            width={700}
            height={380}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="hsl(var(--muted))"
                    stroke="hsl(var(--border))"
                    strokeWidth={0.5}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: 'hsl(var(--accent))' },
                      pressed: { outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>

            {locations.map((location) => (
              <Marker
                key={location.id}
                coordinates={location.coordinates}
                onMouseEnter={(e) => {
                  setHoveredLocation(location);
                  if (mapRef.current) {
                    const rect = mapRef.current.getBoundingClientRect();
                    setTooltipPos({
                      x: e.clientX - rect.left,
                      y: e.clientY - rect.top - 50
                    });
                  }
                }}
                onMouseLeave={() => setHoveredLocation(null)}
              >
                <AnimatePresence>
                  {animatedMarkers.includes(location.id) && (
                    <motion.g
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 20,
                      }}
                    >
                      {/* Outer pulse ring */}
                      <motion.circle
                        r={10}
                        fill="none"
                        stroke="hsl(var(--brand))"
                        strokeWidth={1}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{
                          scale: [0.5, 1.5, 0.5],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                      {/* Main marker */}
                      <circle
                        r={4}
                        fill="hsl(var(--brand))"
                        className="cursor-pointer"
                      />
                      {/* Inner glow */}
                      <circle
                        r={2}
                        fill="hsl(var(--yellow))"
                        opacity={0.9}
                      />
                    </motion.g>
                  )}
                </AnimatePresence>
              </Marker>
            ))}
          </ComposableMap>
          
          {/* HTML Tooltip */}
          <AnimatePresence>
            {hoveredLocation && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="absolute pointer-events-none bg-ink border-b-2 border-yellow rounded px-3 py-2 z-20"
                style={{
                  left: tooltipPos.x,
                  top: tooltipPos.y,
                  transform: 'translateX(-50%)'
                }}
              >
                <p className="text-white text-sm font-semibold whitespace-nowrap">
                  {hoveredLocation.name}
                </p>
                {hoveredLocation.description && (
                  <p className="text-gray-400 text-xs whitespace-nowrap">
                    {hoveredLocation.description}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Location list */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-8"
        >
          {locations.map((location, index) => (
            <motion.span
              key={location.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="text-sm text-muted-foreground"
            >
              {location.name}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
